import type { ChatMessage } from './types';
import {
  CHATBOT_CANCELLATION_POLICY,
  CHATBOT_HOW_IT_WORKS,
  CHATBOT_PRICING,
  CHATBOT_SERVICES,
} from './chatbot-knowledge';
import { EscalationForm } from './components/escalation-form';

interface MessageRendererProps {
  message: ChatMessage;
  sessionId: string;
  onQuickAction: (id: string) => void;
  onNavigateBooking: () => void;
}

export function MessageRenderer({
  message,
  sessionId,
  onQuickAction,
  onNavigateBooking,
}: MessageRendererProps) {
  const isAssistant = message.role === 'assistant';

  if (message.message_type === 'booking_form') {
    return (
      <button
        type="button"
        onClick={onNavigateBooking}
        className="max-w-[90%] rounded-2xl bg-[#FF6A00] px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#e55f00]"
      >
        Book a cleaning →
      </button>
    );
  }

  if (message.message_type === 'escalation_form') {
    return <EscalationForm sessionId={sessionId} />;
  }

  return (
    <div
      className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm ${
        isAssistant
          ? 'bg-white text-gray-900 shadow-sm dark:bg-[#2C2C2C] dark:text-white'
          : 'ml-auto bg-[#FF6A00] text-white'
      }`}
    >
      <p className="whitespace-pre-wrap">{message.content}</p>

      {message.message_type === 'pricing_card' && (
        <ul className="mt-3 space-y-1 text-sm opacity-90">
          {CHATBOT_PRICING.bullets.map(item => (
            <li key={item} className="flex gap-2">
              <span aria-hidden="true">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {message.message_type === 'services_card' && (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm opacity-90">
          {CHATBOT_SERVICES.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}

      {message.message_type === 'how_it_works_card' && (
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm opacity-90">
          {CHATBOT_HOW_IT_WORKS.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      )}

      {message.message_type === 'help_menu' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(
            (message.payload?.options as Array<{
              id: string;
              label: string;
            }>) || []
          ).map(option => (
            <button
              key={option.id}
              type="button"
              className="rounded-full border border-[#FF6A00] px-3 py-1 text-xs text-[#FF6A00]"
              onClick={() => onQuickAction(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {message.content.includes('cancellation') && (
        <p className="mt-2 text-xs opacity-80">{CHATBOT_CANCELLATION_POLICY}</p>
      )}
    </div>
  );
}
